/**
 * MaintenanceTruth — Truth Layer Maintenance Management tab
 *
 * Queries the truth layer (server2) maintenance API for real Aquarius-ingested data.
 * Data Constitution compliant: no fabrication, explicit data provenance.
 */
import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Calendar, TrendingUp, Wrench, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import LineChart from '../components/LineChart';
import DataTable from '../components/DataTable';
import DonutChart from '../components/DonutChart';
import { API_ENDPOINTS } from '../config/api';
import { STATUS_COLORS, CHART_PALETTE } from '../config/colors';
import { formatYmd } from '../utils/ukTime';

/** Calendar day as written by the source (no zone shift): `19 Oct 2026`. */
const DISPLAY_DATE = { day: '2-digit', month: 'short', year: 'numeric' } as const;

// ── Types ────────────────────────────────────────────────────────────────

interface Summary {
  totalMaintenanceCost: number;
  avgCostPerVehicle: number;
  totalDowntimeHours: number;
  maintenanceEvents: number;
  uniqueVehiclesServiced: number;
  avgCostPerEvent: number;
}

interface CostBreakdownItem {
  maintenanceType: string;
  eventCount: number;
  totalCost: number;
  avgCost: number;
  totalDowntime: number;
  avgDowntime: number;
  costPercentage: number;
}

interface TrendPoint {
  period: string;
  eventCount: number;
  totalCost: number;
  totalDowntime: number;
  uniqueVehicles: number;
}

interface MaintenanceEvent {
  event_id: string;
  occurred_at: string;
  subject_entity_id: string;
  maintenance_type: string;
  vehicle_registration: string;
  facility_location: string;
  labor_hours: number;
  labor_cost: number;
  parts_cost: number;
  total_cost: number;
  downtime_hours: number;
  odometer_km: number;
  service_description: string;
  completed_date: string;
  scheduled_date: string;
}

interface UpcomingEvent {
  event_id: string;
  occurred_at: string;
  subject_entity_id: string;
  maintenance_type: string;
  vehicle_registration: string;
  facility_location: string;
  scheduled_date: string;
  is_overdue: string;
  days_until_due: string;
  service_description: string;
}

interface Defect {
  event_id: string;
  occurred_at: string;
  subject_entity_id: string;
  vehicle_registration: string;
  category: string;
  description: string;
  severity: string;
  reported_date: string;
}

interface DriverCheck {
  event_id: string;
  occurred_at: string;
  driver_name: string;
  check_date: string;
  check_result: string;
  vehicle_registration: string;
  subject_entity_id: string;
}

type ActiveView = 'overview' | 'events' | 'defects' | 'upcoming';

// ── Formatting helpers ───────────────────────────────────────────────────

function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `£${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `£${(value / 1_000).toFixed(1)}k`;
  return `£${value.toFixed(0)}`;
}

function formatHours(value: number): string {
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k hrs`;
  return `${value.toFixed(1)} hrs`;
}

// ── Component ────────────────────────────────────────────────────────────

export default function MaintenanceTruth() {
  // State
  const [activeView, setActiveView] = useState<ActiveView>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data
  const [summary, setSummary] = useState<Summary | null>(null);
  const [costBreakdown, setCostBreakdown] = useState<CostBreakdownItem[]>([]);
  const [trends, setTrends] = useState<TrendPoint[]>([]);
  const [events, setEvents] = useState<MaintenanceEvent[]>([]);
  const [upcoming, setUpcoming] = useState<UpcomingEvent[]>([]);
  const [overdue, setOverdue] = useState<UpcomingEvent[]>([]);
  const [defects, setDefects] = useState<Defect[]>([]);
  const [defectsBySeverity, setDefectsBySeverity] = useState<Record<string, number>>({});
  const [driverChecks, setDriverChecks] = useState<DriverCheck[]>([]);
  const [checksByResult, setChecksByResult] = useState<Record<string, number>>({});
  const [defectsSubView, setDefectsSubView] = useState<'outstanding' | 'driver-checks'>('driver-checks');

  // Filters
  const [filterOptions, setFilterOptions] = useState<{ maintenanceTypes: string[]; facilityLocations: string[] }>({ maintenanceTypes: [], facilityLocations: [] });
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedFacility, setSelectedFacility] = useState<string>('');
  const [granularity, setGranularity] = useState<string>('monthly');
  const [trendMetric, setTrendMetric] = useState<'cost' | 'downtime'>('cost');

  // Events pagination
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsTotalPages, setEventsTotalPages] = useState(1);

  // ── Data fetching ──────────────────────────────────────────────────────

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, breakdownRes, trendsRes, filterRes] = await Promise.all([
        fetch(API_ENDPOINTS.maintenanceTruth.summary()),
        fetch(API_ENDPOINTS.maintenanceTruth.costBreakdown()),
        fetch(API_ENDPOINTS.maintenanceTruth.trends(granularity)),
        fetch(API_ENDPOINTS.maintenanceTruth.filterOptions()),
      ]);

      if (!summaryRes.ok || !breakdownRes.ok || !trendsRes.ok) {
        throw new Error('Failed to fetch maintenance data from truth layer');
      }

      const [summaryData, breakdownData, trendsData, filterData] = await Promise.all([
        summaryRes.json(),
        breakdownRes.json(),
        trendsRes.json(),
        filterRes.json(),
      ]);

      setSummary(summaryData);
      setCostBreakdown(breakdownData.breakdown || []);
      setTrends(trendsData.trends || []);
      setFilterOptions(filterData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [granularity]);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.maintenanceTruth.events({
        page: eventsPage,
        pageSize: 50,
        maintenanceType: selectedType || undefined,
        facilityLocation: selectedFacility || undefined,
      }));
      if (!res.ok) throw new Error('Failed to fetch events');
      const data = await res.json();
      setEvents(data.data || []);
      setEventsTotalPages(data.pagination?.totalPages || 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [eventsPage, selectedType, selectedFacility]);

  const fetchDefects = useCallback(async () => {
    setLoading(true);
    try {
      const [defectsRes, checksRes] = await Promise.all([
        fetch(API_ENDPOINTS.maintenanceTruth.defects()),
        fetch(API_ENDPOINTS.maintenanceTruth.defectChecks()),
      ]);
      if (!defectsRes.ok) throw new Error('Failed to fetch defects');
      const defectsData = await defectsRes.json();
      setDefects(defectsData.defects || []);
      setDefectsBySeverity(defectsData.bySeverity || {});
      if (checksRes.ok) {
        const checksData = await checksRes.json();
        setDriverChecks(checksData.checks || []);
        setChecksByResult(checksData.byResult || {});
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUpcoming = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.maintenanceTruth.upcoming());
      if (!res.ok) throw new Error('Failed to fetch upcoming');
      const data = await res.json();
      setUpcoming(data.upcoming || []);
      setOverdue(data.overdue || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Effects ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (activeView === 'overview') fetchOverview();
  }, [activeView, fetchOverview]);

  useEffect(() => {
    if (activeView === 'events') fetchEvents();
  }, [activeView, fetchEvents]);

  useEffect(() => {
    if (activeView === 'defects') fetchDefects();
  }, [activeView, fetchDefects]);

  useEffect(() => {
    if (activeView === 'upcoming') fetchUpcoming();
  }, [activeView, fetchUpcoming]);

  // ── Loading state ──────────────────────────────────────────────────────

  if (loading && !summary) {
    return (
      <div className="space-y-8">
        <div className="flex flex-col items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-success mb-4"></div>
          <div className="text-muted-foreground">Loading truth layer maintenance data...</div>
        </div>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────

  if (error && !summary) {
    return (
      <div className="space-y-8">
        <div className="flex flex-col items-center justify-center h-64">
          <AlertTriangle className="w-12 h-12 text-warning mb-4" />
          <div className="text-muted-foreground mb-2">Failed to load maintenance data</div>
          <div className="text-sm text-muted-foreground">{error}</div>
          <button
            onClick={fetchOverview}
            className="mt-4 px-4 py-2 bg-primary hover:bg-primary text-white rounded-lg text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white mb-1">Maintenance Management</h2>
          <p className="text-muted-foreground text-sm flex items-center gap-2">
            <Shield className="w-4 h-4 text-success" />
            Truth Layer — Observed maintenance data
          </p>
        </div>

        {/* View Tabs */}
        <div className="flex gap-1 bg-card rounded-lg p-1 border border-border">
          {([
            { id: 'overview' as const, label: 'Overview', icon: TrendingUp },
            { id: 'events' as const, label: 'Events', icon: Wrench },
            { id: 'defects' as const, label: 'Defects', icon: AlertTriangle },
            { id: 'upcoming' as const, label: 'Upcoming', icon: Calendar },
          ]).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveView(id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                activeView === id
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* VIEW: Overview */}
      {activeView === 'overview' && summary && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard
              label="Total Maintenance Cost"
              value={formatCurrency(summary.totalMaintenanceCost)}
              subtitle={`${summary.maintenanceEvents} events`}
              color="green"
            />
            <KPICard
              label="Avg Cost per Vehicle"
              value={formatCurrency(summary.avgCostPerVehicle)}
              subtitle={`${summary.uniqueVehiclesServiced} vehicles serviced`}
              color="blue"
            />
            <KPICard
              label="Total Downtime"
              value={formatHours(summary.totalDowntimeHours)}
              subtitle="Vehicle unavailability"
              color="amber"
            />
            <KPICard
              label="Maintenance Events"
              value={String(summary.maintenanceEvents)}
              subtitle="Total service events"
              color="purple"
            />
          </div>

          {/* Cost Distribution + Types Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Cost Distribution */}
            <div className="bg-card rounded-lg shadow-sm p-6 border border-border hover:shadow-lg hover:shadow-success/50 hover:border-success transition-all">
              <h3 className="text-lg font-semibold text-white pb-3">Cost Distribution</h3>
              {costBreakdown.length > 0 ? (
                <div className="[&>div]:bg-transparent [&>div]:border-0 [&>div]:shadow-none [&>div]:p-0">
                  <DonutChart
                    data={costBreakdown.map((item, index) => {
                      const colors = CHART_PALETTE;
                      return { label: item.maintenanceType, value: item.totalCost, color: colors[index % colors.length] };
                    })}
                    title=""
                    legendOffset="-mt-16"
                  />
                </div>
              ) : (
                <div className="text-muted-foreground text-center py-8">No data available</div>
              )}
            </div>

            {/* Types Distribution */}
            <div className="bg-card rounded-lg shadow-sm p-6 border border-border hover:shadow-lg hover:shadow-ring/50 hover:border-ring transition-all">
              <h3 className="text-lg font-semibold text-white pb-3">Maintenance Types</h3>
              {costBreakdown.length > 0 ? (
                <div className="[&>div]:bg-transparent [&>div]:border-0 [&>div]:shadow-none [&>div]:p-0">
                  <DonutChart
                    data={costBreakdown.map((item, index) => {
                      const colors = CHART_PALETTE;
                      return { label: item.maintenanceType, value: item.eventCount, color: colors[index % colors.length] };
                    })}
                    title=""
                    legendOffset="-mt-16"
                  />
                </div>
              ) : (
                <div className="text-muted-foreground text-center py-8">No data available</div>
              )}
            </div>
          </div>

          {/* Trends */}
          <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Maintenance Trends</h3>
              <div className="flex gap-2">
                <select
                  value={granularity}
                  onChange={(e) => setGranularity(e.target.value)}
                  className="px-3 py-1.5 text-sm font-medium rounded-md bg-secondary text-foreground border border-border hover:bg-accent focus:outline-none cursor-pointer"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
                <select
                  value={trendMetric}
                  onChange={(e) => setTrendMetric(e.target.value as 'cost' | 'downtime')}
                  className="px-3 py-1.5 text-sm font-medium rounded-md bg-secondary text-foreground border border-border hover:bg-accent focus:outline-none cursor-pointer"
                >
                  <option value="cost">Total Cost</option>
                  <option value="downtime">Total Downtime</option>
                </select>
              </div>
            </div>
            {trends.length > 0 ? (
              <LineChart
                data={trends.map(t => ({
                  label: t.period,
                  value: trendMetric === 'cost' ? t.totalCost : t.totalDowntime,
                  date: t.period,
                }))}
                title={trendMetric === 'cost' ? 'Total Cost' : 'Total Downtime'}
                yAxisLabel={trendMetric === 'cost' ? 'Cost (£)' : 'Hours'}
                color={trendMetric === 'cost' ? STATUS_COLORS.success : STATUS_COLORS.destructive}
                formatYAxisAsK={true}
              />
            ) : (
              <div className="text-muted-foreground text-center py-8">No trend data available</div>
            )}
          </div>

          {/* Cost Breakdown Table */}
          {costBreakdown.length > 0 && (
            <DataTable
              columns={[
                { key: 'Type', label: 'Maintenance Type' },
                { key: 'Count', label: 'Events', align: 'right' as const },
                { key: 'Total Cost', label: 'Total Cost', align: 'right' as const },
                { key: 'Avg Cost', label: 'Avg Cost', align: 'right' as const },
                { key: 'Avg Downtime', label: 'Avg Downtime', align: 'right' as const },
              ]}
              data={costBreakdown.map(item => ({
                Type: item.maintenanceType,
                Count: item.eventCount,
                'Total Cost': formatCurrency(item.totalCost),
                'Avg Cost': formatCurrency(item.avgCost),
                'Avg Downtime': `${item.avgDowntime.toFixed(1)} hrs`,
              }))}
              title="Cost Breakdown by Type"
              hideExport
            />
          )}
        </>
      )}

      {/* VIEW: Events */}
      {activeView === 'events' && (
        <>
          {/* Filters Row */}
          <div className="bg-card rounded-lg shadow-sm p-4 border border-border">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Maintenance Type</label>
                <select
                  value={selectedType}
                  onChange={(e) => { setSelectedType(e.target.value); setEventsPage(1); }}
                  className="w-full bg-secondary text-foreground rounded px-3 py-2 border border-border focus:border-success focus:outline-none"
                >
                  <option value="">All Types</option>
                  {filterOptions.maintenanceTypes.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Facility Location</label>
                <select
                  value={selectedFacility}
                  onChange={(e) => { setSelectedFacility(e.target.value); setEventsPage(1); }}
                  className="w-full bg-secondary text-foreground rounded px-3 py-2 border border-border focus:border-success focus:outline-none"
                >
                  <option value="">All Facilities</option>
                  {filterOptions.facilityLocations.map(f => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => { setSelectedType(''); setSelectedFacility(''); setEventsPage(1); }}
                  className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-secondary transition-all"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>

          {/* Events Table */}
          <DataTable
            columns={[
              { key: 'Date', label: 'Date' },
              { key: 'Vehicle', label: 'Vehicle' },
              { key: 'Type', label: 'Type' },
              { key: 'Facility', label: 'Facility' },
              { key: 'Cost', label: 'Total Cost', align: 'right' as const },
              { key: 'Downtime', label: 'Downtime', align: 'right' as const },
              { key: 'Description', label: 'Description' },
            ]}
            data={events.map(e => ({
              Date: formatYmd(e.occurred_at, DISPLAY_DATE, '-'),
              Vehicle: e.vehicle_registration || e.subject_entity_id?.substring(0, 8) || '-',
              Type: e.maintenance_type || '-',
              Facility: e.facility_location || '-',
              Cost: e.total_cost ? formatCurrency(e.total_cost) : '-',
              Downtime: e.downtime_hours ? `${e.downtime_hours.toFixed(1)} hrs` : '-',
              Description: e.service_description || '-',
            }))}
            title={`Maintenance Events (Page ${eventsPage} of ${eventsTotalPages})`}
            hideExport
          />

          {/* Pagination */}
          {eventsTotalPages > 1 && (
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => setEventsPage(p => Math.max(1, p - 1))}
                disabled={eventsPage <= 1}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground border border-border rounded disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronUp className="w-4 h-4 -rotate-90" /> Previous
              </button>
              <span className="text-sm text-muted-foreground">
                Page {eventsPage} of {eventsTotalPages}
              </span>
              <button
                onClick={() => setEventsPage(p => Math.min(eventsTotalPages, p + 1))}
                disabled={eventsPage >= eventsTotalPages}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground border border-border rounded disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next <ChevronDown className="w-4 h-4 -rotate-90" />
              </button>
            </div>
          )}
        </>
      )}

      {/* VIEW: Defects */}
      {activeView === 'defects' && (
        <>
          {/* Sub-tab toggle */}
          <div className="flex gap-1 bg-card rounded-lg p-1 border border-border w-fit">
            <button
              onClick={() => setDefectsSubView('driver-checks')}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                defectsSubView === 'driver-checks'
                  ? 'bg-primary text-white'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
              }`}
            >
              Driver Checks ({driverChecks.length})
            </button>
            <button
              onClick={() => setDefectsSubView('outstanding')}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                defectsSubView === 'outstanding'
                  ? 'bg-destructive text-white'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
              }`}
            >
              Outstanding Defects ({defects.length})
            </button>
          </div>

          {/* SUB-VIEW: Driver Checks */}
          {defectsSubView === 'driver-checks' && (
            <>
              {/* Result summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(checksByResult).map(([result, count]) => (
                  <div
                    key={result}
                    className={`bg-card rounded-lg border p-4 ${
                      result === 'pass' ? 'border-success/40' :
                      result === 'fail' ? 'border-destructive/40' :
                      result === 'advisory' ? 'border-warning/40' : 'border-border'
                    }`}
                  >
                    <div className="text-sm text-muted-foreground uppercase">{result}</div>
                    <div className={`text-2xl font-bold ${
                      result === 'pass' ? 'text-success' :
                      result === 'fail' ? 'text-destructive' :
                      result === 'advisory' ? 'text-warning' : 'text-white'
                    }`}>{count}</div>
                  </div>
                ))}
                {Object.keys(checksByResult).length === 0 && (
                  <div className="col-span-4 text-center text-muted-foreground py-4">No driver checks loaded</div>
                )}
              </div>

              <DataTable
                columns={[
                  { key: 'Date', label: 'Check Date' },
                  { key: 'Driver', label: 'Driver' },
                  { key: 'Result', label: 'Result' },
                  { key: 'Vehicle', label: 'Vehicle' },
                ]}
                data={driverChecks.map(c => ({
                  Date: formatYmd(c.check_date || c.occurred_at, DISPLAY_DATE, '-'),
                  Driver: c.driver_name || '-',
                  Result: c.check_result
                    ? c.check_result.charAt(0).toUpperCase() + c.check_result.slice(1)
                    : '-',
                  Vehicle: c.vehicle_registration || '-',
                }))}
                title={`Driver Defect Checks (${driverChecks.length})`}
                hideExport
              />
            </>
          )}

          {/* SUB-VIEW: Outstanding Defects */}
          {defectsSubView === 'outstanding' && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(defectsBySeverity).map(([severity, count]) => (
                  <div
                    key={severity}
                    className={`bg-card rounded-lg border p-4 ${
                      severity.toLowerCase() === 'critical' || severity.toLowerCase() === 'high'
                        ? 'border-destructive/40'
                        : severity.toLowerCase() === 'medium'
                          ? 'border-warning/40'
                          : 'border-border'
                    }`}
                  >
                    <div className="text-sm text-muted-foreground uppercase">{severity}</div>
                    <div className="text-2xl font-bold text-white">{count}</div>
                  </div>
                ))}
                {Object.keys(defectsBySeverity).length === 0 && (
                  <div className="col-span-4 text-center text-muted-foreground py-4">No outstanding defects</div>
                )}
              </div>

              <DataTable
                columns={[
                  { key: 'Reported', label: 'Reported Date' },
                  { key: 'Vehicle', label: 'Vehicle' },
                  { key: 'Category', label: 'Category' },
                  { key: 'Severity', label: 'Severity' },
                  { key: 'Description', label: 'Description' },
                ]}
                data={defects.map(d => ({
                  Reported: d.reported_date || formatYmd(d.occurred_at, DISPLAY_DATE, '-'),
                  Vehicle: d.vehicle_registration || d.subject_entity_id?.substring(0, 8) || '-',
                  Category: d.category || '-',
                  Severity: d.severity || '-',
                  Description: d.description || '-',
                }))}
                title={`Outstanding Defects (${defects.length})`}
                hideExport
              />
            </>
          )}
        </>
      )}

      {/* VIEW: Upcoming */}
      {activeView === 'upcoming' && (
        <>
          {/* Overdue Alert */}
          {overdue.length > 0 && (
            <div className="bg-destructive/20 border border-destructive/40 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-destructive">{overdue.length} Overdue Maintenance Event{overdue.length !== 1 ? 's' : ''}</p>
                <p className="text-sm text-destructive/80 mt-1">
                  These events have passed their scheduled date and require immediate attention.
                </p>
              </div>
            </div>
          )}

          {/* Overdue Table */}
          {overdue.length > 0 && (
            <DataTable
              columns={[
                { key: 'Scheduled', label: 'Scheduled Date' },
                { key: 'Vehicle', label: 'Vehicle' },
                { key: 'Type', label: 'Type' },
                { key: 'Facility', label: 'Facility' },
                { key: 'Description', label: 'Description' },
              ]}
              data={overdue.map(e => ({
                Scheduled: e.scheduled_date || '-',
                Vehicle: e.vehicle_registration || e.subject_entity_id?.substring(0, 8) || '-',
                Type: e.maintenance_type || '-',
                Facility: e.facility_location || '-',
                Description: e.service_description || '-',
              }))}
              title={`Overdue (${overdue.length})`}
              hideExport
            />
          )}

          {/* Upcoming Table */}
          <DataTable
            columns={[
              { key: 'Scheduled', label: 'Scheduled Date' },
              { key: 'Vehicle', label: 'Vehicle' },
              { key: 'Type', label: 'Type' },
              { key: 'Facility', label: 'Facility' },
              { key: 'Description', label: 'Description' },
            ]}
            data={upcoming.map(e => ({
              Scheduled: e.scheduled_date || '-',
              Vehicle: e.vehicle_registration || e.subject_entity_id?.substring(0, 8) || '-',
              Type: e.maintenance_type || '-',
              Facility: e.facility_location || '-',
              Description: e.service_description || '-',
            }))}
            title={`Upcoming Maintenance (${upcoming.length})`}
            hideExport
          />
        </>
      )}

      {/* Data Provenance Footer */}
      <div className="bg-card/50 rounded-lg border border-border/50 p-3 flex items-center gap-2 text-xs text-muted-foreground">
        <Shield className="w-3.5 h-3.5 text-success/60" />
        <span>
          Data Constitution compliant — Observed maintenance data stored in Iceberg truth tables.
          No fabrication. All records carry source provenance and identity resolution metadata.
        </span>
      </div>
    </div>
  );
}

// ── KPI Card sub-component ───────────────────────────────────────────────

function KPICard({ label, value, subtitle, color }: {
  label: string;
  value: string;
  subtitle: string;
  color: 'green' | 'blue' | 'amber' | 'purple';
}) {
  const colorMap = {
    green:  { bg: 'from-success/20',  border: 'border-success/30',  dot: 'bg-success',  glow: 'bg-success/10' },
    blue:   { bg: 'from-chart-10/20',   border: 'border-chart-10/30',   dot: 'bg-chart-10',   glow: 'bg-chart-10/10' },
    amber:  { bg: 'from-chart-2/20',  border: 'border-chart-2/30',  dot: 'bg-chart-2',  glow: 'bg-chart-2/10' },
    purple: { bg: 'from-chart-5/20', border: 'border-chart-5/30', dot: 'bg-chart-5', glow: 'bg-chart-5/10' },
  };
  const c = colorMap[color];

  return (
    <div className={`relative overflow-hidden bg-gradient-to-br ${c.bg} via-muted to-background rounded-xl ${c.border} p-6 shadow-xl`}>
      <div className={`absolute top-0 right-0 w-32 h-32 ${c.glow} rounded-full -mr-16 -mt-16 blur-2xl`}></div>
      <div className="relative">
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-2 h-2 ${c.dot} rounded-full animate-pulse`}></div>
          <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">{label}</div>
        </div>
        <div className="text-4xl font-bold text-white mb-1">{value}</div>
        <div className="text-xs text-muted-foreground font-medium">{subtitle}</div>
      </div>
    </div>
  );
}
